/**
 * LandingNav — sticky brutalist top navigation, rebuilt to FAITHFULLY match the
 * Figma DEVFORGE header (node 1:3), adapted to a LIGHT page + StudySync brand.
 *
 * Figma structure (1440×68, 2px bottom rule):
 *   ┌ logo cell (icon + wordmark), framed by vertical rules ┐
 *   …                       nav links (regular weight, spaced) …
 *   │ "Sign In" framed cell │ full-height filled CTA cell │
 *
 * Adaptations:
 *   dark bg  → white card surface (#FFFFFF) on a #FAFAFA page
 *   cyan CTA → Stanford Teal (#00505C) full-height filled button
 *   DEVFORGE wordmark → StudySync icon + stacked wordmark (Short Logo SVG)
 *
 * ZERO MUI: composed from the vendored RetroUI <Button> + Tailwind utilities.
 */
import * as React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useSecureAuth } from '../../../contexts/SecureAuthContext';

// "Success Stories" and "Projects" point at the Astro stories blog, which is VENDORED
// into this app at `public/stories/` (via `npm run export` in the studysync-stories
// repo) and therefore served SAME-ORIGIN at `/stories/...` on every deploy — prod,
// Vercel preview, and local dev (where setupProxy forwards `/stories` → :4321). So we
// use a relative base: it resolves correctly everywhere and stays consistent with the
// map cards, which already deep-link via relative `/stories/<slug>/`.
const STORIES_SITE = '/stories';
const NAV_LINKS = [
  // "People" is the dedicated /people route (the team page). "Success Stories" and
  // "Projects" point at the Astro stories blog vendored under /stories. (The former
  // in-page "Platform" #features and "Researcher Experience" #dashboard links were
  // removed; those sections still render, they just aren't linked from the nav.)
  { label: 'People', href: '/people' },
  { label: 'Success Stories', href: `${STORIES_SITE}/success-stories/` },
  { label: 'Projects', href: `${STORIES_SITE}/projects/` },
];

/** Height of the sticky nav (matches `h-[88px]`) — in-page scrolls subtract it so the
 *  target section clears the bar instead of hiding behind it. */
const NAV_HEIGHT = 88;

const LandingNav: React.FC = () => {
  // When a researcher is already signed in, the login cell becomes "Go to Dashboard"
  // (mirrors the original landing's signed-in CTA). `/dashboard` is SmartRedirect,
  // which routes to the right place per role/status — approved → researcher dashboard,
  // pending/rejected → application status, master → master dashboard, etc.
  const { user } = useSecureAuth();
  const isAuthenticated = !!user;
  const researcherHref = isAuthenticated ? '/dashboard' : '/researcher/auth';
  const researcherLabel = isAuthenticated ? 'Go to Dashboard' : 'Researcher Login';

  // Mobile-only hamburger overlay. Desktop (lg+) never shows this — it keeps the
  // inline links + framed CTA cells. Below lg the cluster collapses to a single
  // hamburger that toggles this brutalist drop-down panel.
  const [menuOpen, setMenuOpen] = React.useState(false);

  // Respect the OS "reduce motion" preference: when set, the panel renders straight
  // to its final state with no vertical slide (we still fade — a non-vestibular cue).
  const prefersReducedMotion = useReducedMotion();

  /** In-page (#…) links smooth-scroll to their section (offset for the sticky bar)
   *  WHEN that section exists on the current page. If it doesn't — e.g. this nav is
   *  reused on a legal page — fall back to the landing's anchor (`/#features`) so the
   *  link is never dead. Route/external links (no leading #) pass through. */
  const handleAnchorClick = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      if (!href.startsWith('#')) return;
      const target = document.getElementById(href.slice(1));
      if (!target) {
        // Section isn't on this page (e.g. a legal page reusing this nav) → send the
        // user to the landing's anchor instead of scrolling nowhere.
        e.preventDefault();
        window.location.assign(`/${href}`);
        return;
      }
      e.preventDefault();
      const top = target.getBoundingClientRect().top + window.scrollY - NAV_HEIGHT;
      const prefersReduced =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top, behavior: prefersReduced ? 'auto' : 'smooth' });
      // Keep the URL hash in sync (shareable / back-button) without a second jump.
      window.history.pushState(null, '', href);
    },
    [],
  );

  // Close the mobile overlay on Escape, and lock body scroll while it's open so
  // the page behind doesn't scroll under the panel. Both unwind on close/unmount.
  React.useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [menuOpen]);

  return (
    <nav className="sticky top-0 z-50 w-full border-b-2 border-black bg-card">
      {/* Inner rail spans the full width with a fixed height that the framed
          cells (logo / Sign In / CTA) stretch to fill, mirroring the Figma. */}
      <div className="mx-auto flex h-[88px] max-w-[1440px] items-stretch justify-between">
        {/* Logo cell — framed by a vertical rule on its right edge, exactly like
            the Figma's bordered logo column. */}
        <a
          href="#top"
          onClick={(e) => handleAnchorClick(e, '#top')}
          className="flex items-center gap-3 border-l-2 border-r-2 border-black px-8 no-underline"
          aria-label="StudySync home"
        >
          <img
            src="/finalized_logo/Study Sync Short Logo.svg"
            alt="StudySync"
            className="h-12 w-auto"
          />
        </a>

        {/* Right cluster: links + Sign In cell + filled CTA cell. */}
        <div className="flex flex-1 items-stretch justify-end">
          {/* Nav links — regular weight, NOT uppercase, evenly spaced (Figma). */}
          <div className="hidden items-center gap-9 px-8 lg:flex">
            {NAV_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleAnchorClick(e, link.href)}
                className="whitespace-nowrap font-sans text-[0.95rem] text-foreground no-underline transition-colors hover:text-primary"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Researcher cell — filled Cardinal block. Desktop (lg+) only: "Researcher
              Login" (flips to "Go to Dashboard" once a researcher is authenticated).
              Below lg this is replaced by the hamburger overlay. */}
          <a
            href={researcherHref}
            className="hidden items-center border-l-2 border-solid border-black bg-primary px-7 font-sans text-base font-bold text-primary-foreground no-underline transition-colors hover:bg-primary-hover lg:flex"
          >
            {researcherLabel}
          </a>

          {/* Filled Teal CTA cell — desktop (lg+) only: the "Join as Participant" enroll
              CTA. The Figma's filled "Get Started" block, recolored. Below lg this is
              replaced by the hamburger overlay. */}
          <a
            href="/participant/enroll"
            className="hidden items-center border-l-2 border-r-2 border-black bg-secondary px-6 font-sans text-base font-bold text-secondary-foreground no-underline transition-colors hover:bg-secondary-hover lg:flex"
          >
            Join as Participant
          </a>

          {/* Mobile hamburger — the right-most framed cell below lg. Mirrors the nav
              cell aesthetic (vertical rules + padding) and toggles the overlay panel. */}
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            className="flex items-center justify-center border-l-2 border-r-2 border-solid border-black bg-card px-6 text-foreground transition-colors hover:bg-muted lg:hidden"
          >
            {/* Hamburger ↔ X: three bars that translate to center + rotate ±45° into
                an X via pure CSS transforms (GPU-accelerated, smooth). */}
            <span className="relative block h-[18px] w-7" aria-hidden>
              <span
                className={`absolute left-0 top-0 h-[3px] w-full bg-current transition-transform duration-300 ease-in-out ${
                  menuOpen ? 'translate-y-[7.5px] rotate-45' : ''
                }`}
              />
              <span
                className={`absolute left-0 top-1/2 h-[3px] w-full -translate-y-1/2 bg-current transition-opacity duration-200 ease-in-out ${
                  menuOpen ? 'opacity-0' : 'opacity-100'
                }`}
              />
              <span
                className={`absolute bottom-0 left-0 h-[3px] w-full bg-current transition-transform duration-300 ease-in-out ${
                  menuOpen ? '-translate-y-[7.5px] -rotate-45' : ''
                }`}
              />
            </span>
          </button>
        </div>
      </div>

      {/* Mobile overlay panel — brutalist drop-down under the 88px nav, mobile only.
          A white surface read as ONE cohesive bordered block: every row a 2px black
          divider, left-aligned bold-uppercase content, brand-fill rows for the CTAs.
          Slides down + fades in on open (reverses on close); the slide is gated behind
          the reduce-motion preference. Every row closes the menu on click.
          AnimatePresence wraps the conditional so the exit animation can run. */}
      <AnimatePresence>
        {menuOpen && (
          <div className="lg:hidden">
            {/* Dimmed full-screen backdrop below the sticky nav — tapping it closes the
                menu. Fades in/out independently of the panel. */}
            <motion.button
              type="button"
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
              className="fixed inset-x-0 bottom-0 top-[88px] z-30 cursor-default bg-black/50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
            />
            {/* Brutalist menu panel, sitting on top of the backdrop. A border-t-2 closes
                the seam under the nav's own bottom rule so the block reads as one. */}
            <motion.div
              className="fixed inset-x-0 top-[88px] z-40 border-t-2 border-b-2 border-solid border-black bg-card shadow-lg"
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: prefersReducedMotion ? 0 : -8 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
            >
              {NAV_LINKS.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => {
                    handleAnchorClick(e, link.href);
                    setMenuOpen(false);
                  }}
                  className="block border-b-2 border-solid border-black px-6 py-5 text-left font-sans text-base font-bold uppercase tracking-wide text-foreground no-underline transition-colors hover:bg-muted"
                >
                  {link.label}
                </a>
              ))}

              {/* Contact Us — Cardinal fill (was the mobile researcher slot). */}
              <a
                href="mailto:support@studysyncs.com"
                onClick={() => setMenuOpen(false)}
                className="block border-b-2 border-solid border-black bg-primary px-6 py-5 text-left font-sans text-base font-bold uppercase tracking-wide text-primary-foreground no-underline transition-colors hover:bg-primary-hover"
              >
                Contact Us
              </a>

              {/* Download the App — full-width App Store badge: the prominent mobile
                  CTA, spanning the whole available row width. */}
              <a
                href="https://apps.apple.com/au/app/studysync-snyder-lab/id6756796122"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-center border-b-2 border-solid border-black px-6 py-5 no-underline transition-colors hover:bg-muted"
              >
                <img
                  src="/landing/app-store-badge.svg"
                  alt="Download on the App Store"
                  className="h-auto w-full"
                />
              </a>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default LandingNav;
